import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CoachingSession {
  id: string;
  session_id: string;
  agent_id: string;
  template_id: string;
  user_id: string;
  auth_user_id: string;
  company_id: string | null;
  status: 'in_progress' | 'generating' | 'completed' | 'failed';
  session_start: string;
  session_end: string | null;
  conversation_transcript: any;
  collected_data: any;
  document_title: string | null;
  document_content: any;
  document_generated_at: string | null;
  readiness_score: number | null;
  scoring_details: any;
  created_at: string;
  updated_at: string;
  coaching_templates?: {
    name: string;
    template_type: string;
    category?: string;
    output_document?: string;
  };
  simulation_agents?: {
    name?: string | null;
  } | null;
}

export const useCoachingSession = (sessionId: string) => {
  return useQuery({
    queryKey: ['coaching-session', sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coaching_sessions')
        .select(`
          *,
          coaching_templates (name, template_type)
        `)
        .eq('session_id', sessionId)
        .maybeSingle();
        
      if (error) throw error;
      return data as CoachingSession | null;
    },
    enabled: !!sessionId
  });
};

export const useCoachingSessions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['coaching-sessions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return [];
      
      const { data, error } = await supabase
        .from('coaching_sessions')
        .select(`
          *,
          coaching_templates (name, template_type, category, output_document),
          simulation_agents!coaching_sessions_agent_id_fkey (name)
        `)
        .eq('auth_user_id', authData.user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as CoachingSession[];
    },
    enabled: !!user?.id
  });
};
